import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Video, Plus, Trash2, Play, Crown, Upload, Link2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function getEmbedUrl(video) {
  if (video.embed_url) return video.embed_url;
  if (video.video_file_url) return video.video_file_url;
  const url = video.video_url || "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

function isUploadedVideo(video) {
  return video.video_source === "upload" || !!video.video_file_url;
}

export default function CoachExerciseVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", video_url: "" });
  const [sourceMode, setSourceMode] = useState("url");
  const [videoFile, setVideoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [proError, setProError] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVideos = () => {
    api.get("/exercise-videos")
      .then((r) => { setVideos(r.data || []); setProError(false); })
      .catch((err) => {
        if (err.response?.status === 403) setProError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchVideos(); }, []);

  const resetForm = () => {
    setForm({ title: "", description: "", video_url: "" });
    setSourceMode("url");
    setVideoFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Seuls les fichiers vidéo sont acceptés (MP4, WebM, MOV)");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Vidéo max 100 Mo");
      return;
    }
    setVideoFile(file);
    setSourceMode("file");
    e.target.value = "";
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    if (sourceMode === "url" && !form.video_url.trim()) {
      toast.error("Indiquez une URL ou uploadez un fichier vidéo");
      return;
    }
    if (sourceMode === "file" && !videoFile) {
      toast.error("Sélectionnez un fichier vidéo");
      return;
    }

    setSaving(true);
    try {
      if (sourceMode === "file" && videoFile) {
        const fd = new FormData();
        fd.append("title", form.title.trim());
        fd.append("description", form.description.trim());
        fd.append("video_file", videoFile);
        await api.post("/exercise-videos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/exercise-videos", form);
      }
      toast.success("Vidéo ajoutée");
      setOpen(false);
      resetForm();
      fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Erreur — fonction Pro requise");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette vidéo ?")) return;
    try {
      await api.delete(`/exercise-videos/${id}`);
      toast.success("Supprimée");
      fetchVideos();
    } catch {
      toast.error("Erreur");
    }
  };

  if (proError) {
    return (
      <div className="text-center py-16">
        <Crown className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h2 className="font-heading text-xl font-bold text-slate-900">Fonction Pro</h2>
        <p className="text-slate-500 mt-2 text-sm">La bibliothèque vidéo est réservée aux coachs Pro.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Vidéos exercices</h1>
          <p className="text-slate-500 mt-1">{videos.length} vidéo{videos.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 mt-8">Chargement...</p>
      ) : videos.length === 0 ? (
        <div className="text-center py-16 mt-6">
          <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400">Ajoutez des vidéos YouTube/Vimeo ou uploadez vos propres fichiers.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {videos.map((v) => (
            <Card key={v.video_id} className="border-slate-100 overflow-hidden">
              {v.photo_url && (
                <img src={`${API_BASE}${v.photo_url}`} alt="" className="w-full h-32 object-cover" />
              )}
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  {isUploadedVideo(v) ? (
                    <Badge className="text-[10px] bg-purple-50 text-purple-600 border-purple-100" variant="outline">Fichier</Badge>
                  ) : (
                    <Badge className="text-[10px] bg-red-50 text-red-600 border-red-100" variant="outline">{v.video_source || "URL"}</Badge>
                  )}
                </div>
                <h3 className="font-semibold text-slate-900">{v.title}</h3>
                {v.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description}</p>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => setPreview(v)}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Voir
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(v.video_id)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle vidéo</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <Label>Titre de l'exercice</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" />
            </div>

            <div>
              <Label>Source vidéo</Label>
              <div className="flex gap-2 mt-1.5">
                <Button
                  type="button"
                  variant={sourceMode === "url" ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => { setSourceMode("url"); setVideoFile(null); }}
                >
                  <Link2 className="w-3.5 h-3.5 mr-1" /> URL
                </Button>
                <Button
                  type="button"
                  variant={sourceMode === "file" ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => { setSourceMode("file"); setForm({ ...form, video_url: "" }); }}
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> Fichier
                </Button>
              </div>
            </div>

            {sourceMode === "url" ? (
              <div>
                <Label>URL YouTube ou Vimeo</Label>
                <Input
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="mt-1.5"
                />
              </div>
            ) : (
              <div>
                <Label>Fichier vidéo (MP4, WebM, MOV — max 100 Mo)</Label>
                <input ref={fileInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/*" className="hidden" onChange={handleFileSelect} />
                <Button type="button" variant="outline" className="w-full mt-1.5 h-11" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  {videoFile ? videoFile.name : "Choisir un fichier"}
                </Button>
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full">
              {saving ? "..." : "Ajouter"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{preview?.title}</DialogTitle></DialogHeader>
          {preview && (
            <div className="aspect-video rounded-xl overflow-hidden bg-black mt-2">
              {isUploadedVideo(preview) ? (
                <video src={getEmbedUrl(preview)} controls className="w-full h-full" />
              ) : (
                <iframe src={getEmbedUrl(preview)} title={preview.title} className="w-full h-full" allowFullScreen />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

